import { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useDispatch, useSelector } from 'react-redux';
import { setAttachmentsConfig } from 'store/services/chat/actions';
import { getAttachmentsConfig } from 'store/services/chat/selectors';

interface UseDropFilesProps {
  maxFiles: number;
  allowedTypes: string[];
  attachmentsEnabled: boolean;
  attachmentsRichEnabled: boolean;
}

export const useDropFiles = ({
  maxFiles,
  allowedTypes,
  attachmentsEnabled,
  attachmentsRichEnabled,
}: UseDropFilesProps) => {
  const [selectedFiles, setSelectedFiles] = useState([]);

  const dispatch = useDispatch();

  const filterNewFiles = (accepted: File[], existingFiles: File[]) => {
    return accepted.filter(
      (file: File) => !existingFiles.some(f => f.name === file.name),
    );
  };

  const onDropAccepted = useCallback(accepted => {
    setSelectedFiles(files => {
      const newFiles = filterNewFiles(accepted, files);
      return [...files, ...newFiles];
    });
  }, []);

  const onDropRejected = useCallback(rejected => {
    setSelectedFiles(files => {
      const mappedRejected = rejected.map(r => r.file);
      const newFiles = filterNewFiles(mappedRejected, files);
      return [...files, ...newFiles];
    });
  }, []);
  const transformedFileTypes = allowedTypes.reduce((acc, curr) => {
    return {
      ...acc,
      [curr]: [],
    };
  }, {});

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDropAccepted,
    onDropRejected,
    accept: transformedFileTypes,
    maxFiles,
    multiple: maxFiles > 1,
    noClick: true,
  });

  useEffect(() => {
    if (selectedFiles.length) {
      dispatch(
        setAttachmentsConfig({
          attachmentsRichEnabled: !(selectedFiles.length > maxFiles),
        }),
      );
    }
  }, [selectedFiles, maxFiles]);

  const rootProps =
    attachmentsEnabled || attachmentsRichEnabled ? getRootProps() : null;

  const inputProps =
    attachmentsEnabled || attachmentsRichEnabled ? getInputProps : null;

  return {
    isDragActive,
    open,
    setSelectedFiles,
    selectedFiles,
    rootProps,
    inputProps,
  };
};
